import { useState } from 'react'

/**
 * The Taboshi Seed artwork, exactly as the deployed SEED metadata names it.
 *
 * SOURCE OF TRUTH. `TaboshiSeeds.uri(1)` resolves to a metadata document whose
 * `image` field is `ipfs://bafybeia2vbtwka4vdbnwjukzmgobhd7l7ipd7rg7cwhvtijxfv5o4oqhci`.
 * That CID is what is shown here; nothing else is substituted for it.
 *
 * ⚠️ AS OF 2026-08-14 THAT CID DOES NOT RESOLVE. The metadata document itself
 * fetches fine (HTTP 200), but its image returned 504/404 from seven public
 * gateways over a three-minute allowance — it appears not to be pinned. Wallets
 * and marketplaces will show a broken image for SEED until it is.
 *
 * So this component degrades rather than breaking the page:
 *   1. a local copy at `public/taboshi-seed.png`, if one has been dropped in —
 *      no code change needed, it is picked up automatically and loads instantly;
 *   2. otherwise the public gateway, which will start working the moment the
 *      CID is pinned;
 *   3. otherwise nothing at all. A missing image renders as absence, never as a
 *      browser's broken-image icon, which would read as a broken page.
 */
const LOCAL = `${import.meta.env.BASE_URL}taboshi-seed.png`
const IPFS_CID = 'bafybeia2vbtwka4vdbnwjukzmgobhd7l7ipd7rg7cwhvtijxfv5o4oqhci'
const GATEWAY = `https://ipfs.io/ipfs/${IPFS_CID}`

export function SeedMark() {
  const [stage, setStage] = useState<0 | 1 | 2>(0)
  const [loaded, setLoaded] = useState(false)
  if (stage === 2) return null

  return (
    <img
      // Hidden until it has actually decoded. Without this the element reserves
      // its 150px while a gateway request hangs — and the gateway currently
      // hangs for tens of seconds before failing — leaving a blank hole in the
      // middle of the page that looks like a layout bug. Absent is better than
      // pending.
      className={loaded ? 'seedmark' : 'seedmark pending'}
      src={stage === 0 ? LOCAL : GATEWAY}
      alt="taboshi seed"
      width={150}
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => {
        setLoaded(false)
        setStage((s) => (s === 0 ? 1 : 2))
      }}
    />
  )
}
